import React, { useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { fetchOnboarding } from '../../store/onboarding/actions';

import { AppState } from '../../store';
import PrescriptionInterface from '../../interfaces/prescription';

import Menu from '../../components/application/Menu';
import PrescriptionItemList from '../../components/application/Prescription/ItemList';
import DispensationHistory from '../../components/application/Dispensation/History';
import DispensationCancellation from '../../components/application/Dispensation/Cancellation';

import Icon from '../../components/ui/Icon';
import Page from '../../components/ui/Page';
import Text from '../../components/ui/Text';
import Button from '../../components/ui/Button';
import Subtitle from '../../components/ui/Subtitle';
import Spinner from '../../components/ui/Spinner';
import DialogModal, { DialogSection, DialogTitle, DialogActions } from '../../components/ui/DialogModal';
import LabelValueList, { ItemInterface as HeaderItemInterface } from '../../components/ui/List/LabelValue';

import * as Styles from './PrescriptionStyles';

const modalStyle = {
  width: '500px',
};

export interface Props {
  readonly error: boolean;
  readonly loading: boolean;
  readonly prescription: PrescriptionInterface;
  readonly headerItems: HeaderItemInterface[];
  readonly dispensePrescription: Function;
  readonly dispensation: {
    errors: string[];
    loading: boolean;
  };
}

const Prescription: React.FC<Props> = ({
  error,
  loading,
  prescription,
  headerItems,
  dispensation,
  dispensePrescription,
}: Props) => {
  const dispatch = useDispatch();
  const [showConfirmDispensationModal, setShowConfirmDispensationModal] = useState(false);
  const canDispenseWithoutSign = useSelector(({ permissions }: AppState) => {
    if (!permissions.current_drugstore) {
      return false;
    }

    return Boolean(permissions.current_drugstore.chain.can_dispense_without_signed);
  });
  const confirmModalRef = useRef<HTMLDivElement>(null!);

  // caso o a farmácia tenha habilitado a opção de dispensar receitas não assinadas
  // precisamos verificar se existe itens onde a receita precisa ser retida para mostrarmos
  // a modal de alerta para avisar o usuário que a receita não foi assinada
  // essa regra se aplica para qualquer medicamento de receituário diferente de simples
  const requireRecipeRetentionItems = useMemo(() => {
    return prescription.items.filter((item) => item.recipe.code !== 'SIMPLES' && item.recipe.code !== null);
  }, [prescription.items]);

  // caso a prescrição não estiver assinada e possuir itens que precisam de retenção de receita
  const shouldWarnAboutNotSignedPrescription = !prescription.signed && requireRecipeRetentionItems.length > 0;

  const hasDisabledPrintAll = (p: PrescriptionInterface): boolean => {
    const hasItemDispensed = p.items.some((item) => item.dispensed === true);

    return !hasItemDispensed || !p.document.url;
  };

  const getTitleDownloadPDF = (p: PrescriptionInterface): string => {
    const hasItemDispensed = p.items.some((item) => item.dispensed === true);

    if (!hasItemDispensed) {
      return 'É necessário dispensar algum medicamento para ter acesso ao PDF';
    }

    if (p.document.url) {
      return 'Clique para realizar o download do PDF da receita completa';
    }

    return 'O PDF da receita não está disponível para download';
  };

  const getTitleValidateSignature = (p: PrescriptionInterface): string => {
    const hasItemDispensed = p.items.some((item) => item.dispensed === true);

    if (!hasItemDispensed) {
      return 'É necessário dispensar algum medicamento para ter acesso ao validador de assinatura';
    }

    if (p.document.url) {
      return 'Clique para acessar o validador de assinatura';
    }

    return 'O PDF da receita não está disponível para download';
  };

  const handleDispenseClick = (): void => {
    if (canDispenseWithoutSign && shouldWarnAboutNotSignedPrescription) {
      setShowConfirmDispensationModal(true);
      return;
    }

    dispensePrescription();
  };

  const handleCancel = (): void => {
    setShowConfirmDispensationModal(false);
  };

  const handleContinue = (): void => {
    setShowConfirmDispensationModal(false);
    dispensePrescription();
  };

  MdHub.event.add('core:moduleHide', (module) => {
    if (module.moduleName === 'platform.onboarding-modal') {
      dispatch(fetchOnboarding());
    }
  });

  return (
    <Styles.Container>
      {!loading && <Menu />}

      <div className="tracking-confirm-dispensation-without-signature-modal" ref={confirmModalRef}>
        <DialogModal show={showConfirmDispensationModal} modalStyle={modalStyle}>
          <DialogTitle>Atenção</DialogTitle>
          <DialogSection>
            <Text color="black" as="div">
              Essa receita não foi assinada digitalmente e possui medicamentos que exigem retenção da receita física
              assinada.
              {requireRecipeRetentionItems.length > 0 && (
                <Styles.List>
                  {requireRecipeRetentionItems.map((item) => (
                    <li key={item.id}>{item.name}</li>
                  ))}
                </Styles.List>
              )}
              Deseja dispensá-la mesmo assim?
            </Text>
          </DialogSection>
          <DialogActions>
            <Button
              type="button"
              size="small"
              variant="flat"
              onClick={handleCancel}
              className="tracking-confirm-dispensation-without-signature-button-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              color="primary"
              size="small"
              variant="flat"
              onClick={handleContinue}
              className="tracking-confirm-dispensation-without-signature-button-confirm"
            >
              Continuar
            </Button>
          </DialogActions>
        </DialogModal>
      </div>

      <Page error={error} loading={loading}>
        <>
          <Styles.Header>
            <Text color="black" scale="subtitle2">
              Receita Digital
            </Text>
            <Styles.Title>{prescription.token}</Styles.Title>

            <Styles.Actions>
              <LabelValueList items={headerItems} />
            </Styles.Actions>
          </Styles.Header>

          {!canDispenseWithoutSign && shouldWarnAboutNotSignedPrescription && (
            <Styles.Box>
              <p>
                Prescrição não assinada digitalmente <Icon name="alert" />
              </p>
              Por esse motivo, medicamentos com necessidade de duas vias ou de tipo não identificado não poderão ser
              dispensados.
            </Styles.Box>
          )}

          <Subtitle marginTop={32}>Prescrição</Subtitle>
          <PrescriptionItemList />

          {dispensation.errors.length > 0 && (
            <Styles.Error type="error">
              Não foi possível fazer a dispensação dos itens selecionados.
              {dispensation.errors.map((err: string) => (
                <Text color="red" key={err}>
                  - {err}
                </Text>
              ))}
            </Styles.Error>
          )}

          <Styles.Actions>
            <Button disabled={dispensation.loading} onClick={handleDispenseClick} minWidth="227px" height={55}>
              {dispensation.loading ? <Spinner size="14px" /> : 'Dispensar Medicamentos'}
            </Button>
            <Button
              as="a"
              target="_blank"
              aria-disabled={hasDisabledPrintAll(prescription)}
              className={hasDisabledPrintAll(prescription) ? ' disabled-link' : ''}
              disabled={hasDisabledPrintAll(prescription)}
              href={hasDisabledPrintAll(prescription) ? `/#/` : 'https://validador.memed.com.br/'}
              onClick={(e: any) =>
                !prescription.document.url || hasDisabledPrintAll(prescription) ? e.preventDefault() : ''
              }
              title={getTitleValidateSignature(prescription)}
            >
              Validar assinatura
            </Button>
            <Button
              as="a"
              aria-disabled={hasDisabledPrintAll(prescription)}
              className={`tracking-download-pdf-dispensation${
                hasDisabledPrintAll(prescription) ? ' disabled-link' : ''
              }`}
              disabled={hasDisabledPrintAll(prescription)}
              download={hasDisabledPrintAll(prescription)}
              href={hasDisabledPrintAll(prescription) ? `/#/` : prescription.document.url}
              onClick={(e: any) =>
                !prescription.document.url || hasDisabledPrintAll(prescription) ? e.preventDefault() : ''
              }
              target="_blank"
              title={getTitleDownloadPDF(prescription)}
            >
              Baixar prescrição completa
            </Button>
          </Styles.Actions>

          <DispensationCancellation />
          <DispensationHistory />
        </>
      </Page>
    </Styles.Container>
  );
};

export default Prescription;
